import React, { useEffect, useMemo, useState } from "react";
import "./FinancialTab.scss";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";
import {
  Card,
  CardContent,
  CardHeader,
  Typography
} from "@mui/material";
import { AiOutlineFilePdf, AiOutlinePrinter } from "react-icons/ai";
import { BsFiletypeXls } from "react-icons/bs";
import { format } from "date-fns";
import {
  Bar,
  BarChart,
  Cell,
  CartesianGrid,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as ReTooltip,
  XAxis,
  YAxis,
  Legend
} from "recharts";

import KpiCard from "./KPICard";
import {
  computeFinancialKpis,
  getFinancialChartSeries,
  listFinancialTransactionsPage
} from "../Services/ClientCardServices";
import { logAuditEvent } from "../Services/ExportService";
import ReusableAccountBooksGrid from "../../../../Components/ReusableAccountBooksGrid";
import { AddActionsButton } from "../../../../Components/AddElementsButton";
import { getActionTypeLabel } from "../../../../OtherData/actionTypeMap";

const money = (n) =>
  new Intl.NumberFormat(undefined, { style: "currency", currency: "EUR", maximumFractionDigits: 2 }).format(n || 0);

const currencyFormatter = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const formatCurrency = (value) => currencyFormatter.format(Number(value || 0));

const getCssThemeColor = (name, fallback) => {
  if (typeof window === "undefined") return fallback;
  const value = window.getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return value || fallback;
};

const readFinancialThemeColors = () => ({
  chartGrid: getCssThemeColor("--cc-chart-grid", "#dbe7e1"),
  chartAxis: getCssThemeColor("--cc-chart-axis", "#4b5f57"),
  chartCursor: getCssThemeColor("--cc-chart-cursor", "#7a9e8f"),
  chartLine: getCssThemeColor("--cc-chart-line", "#1f3a36"),
  chartDebit: getCssThemeColor("--cc-chart-bar-debit", "#d89a2e"),
  chartCredit: getCssThemeColor("--cc-chart-bar-credit", "#3b8c6f"),
  piePositive: getCssThemeColor("--cc-chart-pie-positive", "#3b8c6f"),
  pieNegative: getCssThemeColor("--cc-chart-pie-negative", "#c85d5d"),
  pieNeutral: getCssThemeColor("--cc-chart-line", "#1f3a36"),
  cardBg: getCssThemeColor("--cc-card-bg", "#ffffff")
});

const moneyAxis = (n) =>
  new Intl.NumberFormat(undefined, { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);

const toNumeric = (value) => {
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  const raw = String(value ?? "").trim();
  if (!raw) return 0;

  const normalized = raw
    .replace(/\s+/g, "")
    .replace(/\.(?=\d{3}(?:\D|$))/g, "")
    .replace(",", ".");

  const n = Number(normalized);
  return Number.isFinite(n) ? n : 0;
};

const dayMonthLabel = (value) => {
  const raw = String(value || "");
  const base = raw.includes("T") ? raw.split("T")[0] : raw;
  if (/^\d{4}-\d{2}-\d{2}$/.test(base)) {
    const parts = base.split("-");
    return `${parts[2]}.${parts[1]}`;
  }
  if (/^\d{2}[./-]\d{2}[./-]\d{4}$/.test(base)) {
    const parts = base.split(/[./-]/);
    return `${parts[0]}.${parts[1]}`;
  }
  return base;
};

const monthNumberLabel = (value) => {
  const raw = String(value || "");
  const trimmed = raw.trim();
  if (/^\d{1,2}$/.test(trimmed)) {
    const monthNo = Number(trimmed);
    return monthNo >= 1 && monthNo <= 12 ? String(monthNo) : "";
  }

  if (/^\d{4}-\d{2}$/.test(trimmed)) {
    const monthNo = Number(trimmed.slice(5, 7));
    return monthNo >= 1 && monthNo <= 12 ? String(monthNo) : "";
  }

  const monthNameWithNumber = trimmed.match(/(?:^|\s)(\d{1,2})$/);
  if (monthNameWithNumber) {
    const monthNo = Number(monthNameWithNumber[1]);
    return monthNo >= 1 && monthNo <= 12 ? String(monthNo) : "";
  }

  const base = raw.includes("T") ? raw.split("T")[0] : raw;
  if (/^\d{4}-\d{2}-\d{2}$/.test(base)) {
    const parts = base.split("-");
    return String(Number(parts[1]));
  }
  if (/^\d{2}[./-]\d{2}[./-]\d{4}$/.test(base)) {
    const parts = base.split(/[./-]/);
    return String(Number(parts[1]));
  }
  if (/^\d{2}[./-]\d{4}$/.test(base)) {
    const parts = base.split(/[./-]/);
    return String(Number(parts[0]));
  }
  return "";
};

const monthNameByNumber = (value) => {
  const monthNo = Number(value);
  const monthNames = [
    "Janar",
    "Shkurt",
    "Mars",
    "Prill",
    "Maj",
    "Qershor",
    "Korrik",
    "Gusht",
    "Shtator",
    "Tetor",
    "Nentor",
    "Dhjetor"
  ];
  if (monthNo >= 1 && monthNo <= 12) return monthNames[monthNo - 1];
  return String(value || "—");
};

function BalanceChartTooltip({ active, payload, label }) {
  if (!active || !payload || payload.length === 0) return null;

  const point = payload[0]?.payload;
  return (
    <div className="ftab-chart-tooltip ftab-chart-tooltip--rich">
      <div className="ftab-chart-tooltip-title">{point?.tooltipLabel || label || "—"}</div>
      <div className="ftab-chart-tooltip-row">
        <span className="ftab-chart-tooltip-dot" style={{ backgroundColor: "var(--cc-chart-line)" }} />
        <span className="ftab-chart-tooltip-key">Bilanci</span>
        <span className="ftab-chart-tooltip-value">{money(point?.balance || 0)}</span>
      </div>
    </div>
  );
}

function GenericChartTooltip({ active, payload, label, valueFormatter = (value) => value, labelFormatter }) {
  if (!active || !Array.isArray(payload) || payload.length === 0) return null;

  const title = labelFormatter ? labelFormatter(label, payload) : label;
  return (
    <div className="ftab-chart-tooltip ftab-chart-tooltip--rich">
      <div className="ftab-chart-tooltip-title">{title || "—"}</div>
      {payload.map((entry) => (
        <div key={`${entry?.dataKey || entry?.name}-${entry?.value}`} className="ftab-chart-tooltip-row">
          <span className="ftab-chart-tooltip-dot" style={{ backgroundColor: entry?.color || "var(--cc-chart-line)" }} />
          <span className="ftab-chart-tooltip-key">{entry?.name || entry?.dataKey || "Vlera"}</span>
          <span className="ftab-chart-tooltip-value">{valueFormatter(entry?.value)}</span>
        </div>
      ))}
    </div>
  );
}

function InteractiveLegend({ items, onToggle, isHidden }) {
  return (
    <div className="ftab-legend-wrap">
      {items.map((item) => {
        const hidden = Boolean(isHidden(item.key));
        return (
          <button
            key={item.key}
            type="button"
            className={`ftab-legend-item${hidden ? " is-hidden" : ""}`}
            onClick={() => onToggle(item.key)}
          >
            <span className="ftab-legend-swatch" style={{ backgroundColor: item.color }} />
            <span className="ftab-legend-text">{item.label}</span>
          </button>
        );
      })}
    </div>
  );
}

function PieValueLabel({ cx, cy, midAngle, innerRadius, outerRadius, value }) {
  const numericValue = Number(value || 0);
  if (!numericValue) {
    return null;
  }

  const radius = Number(innerRadius || 0) + (Number(outerRadius || 0) - Number(innerRadius || 0)) * 0.58;
  const radians = (Math.PI / 180) * -midAngle;
  const x = cx + radius * Math.cos(radians);
  const y = cy + radius * Math.sin(radians);

  return (
    <text
      x={x}
      y={y}
      className="ftab-pie-value-label"
      textAnchor="middle"
      dominantBaseline="central"
    >
      {formatCurrency(numericValue)}
    </text>
  );
}

function formatDateForDisplay(date) {
  if (!date) return "";

  if (date instanceof Date) {
    if (Number.isNaN(date.getTime())) return "";
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  }

  const raw = String(date).trim();
  if (!raw) return "";

  const dottedMatch = raw.match(/^(\d{2})[.](\d{2})[.](\d{4})$/);
  if (dottedMatch) return raw;

  const slashMatch = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (slashMatch) {
    const [, day, month, year] = slashMatch;
    return `${day}.${month}.${year}`;
  }

  const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    return `${day}.${month}.${year}`;
  }

  const parsedDate = new Date(raw);
  if (Number.isNaN(parsedDate.getTime())) return raw;

  const day = String(parsedDate.getDate()).padStart(2, "0");
  const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
  const year = parsedDate.getFullYear();
  return `${day}.${month}.${year}`;
}

export default function FinancialTab({ client, dateRange, fiscalYear, canExport = true, canPrint = true }) {
  const clientId =
    client?.id ?? client?.AccountID ?? client?.accountID ?? client?.AccountId ?? client?.accountId ?? null;

  const [kpisLoading, setKpisLoading] = useState(true);
  const [kpisError, setKpisError] = useState(null);
  const [kpis, setKpis] = useState(null);

  const [seriesLoading, setSeriesLoading] = useState(true);
  const [series, setSeries] = useState({ balanceOverTime: [], monthlyMovement: [] });

  const [tableLoading, setTableLoading] = useState(true);
  const [tableError, setTableError] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [themeColors, setThemeColors] = useState(() => readFinancialThemeColors());

  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 50 });
  const [sortModel, setSortModel] = useState([]);
  const [filterModel, setFilterModel] = useState({ items: [] });
  const [quickFilter, setQuickFilter] = useState([]);
  const [monthlySeriesVisibility, setMonthlySeriesVisibility] = useState({
    teArketueshme: true,
    arketimet: true
  });
  const [pieSeriesVisibility, setPieSeriesVisibility] = useState({
    Debi: true,
    Kredi: true,
    Bilanci: true
  });
  const [balanceSeriesVisible, setBalanceSeriesVisible] = useState(true);
  const printId = useMemo(() => `print-financial-${clientId || ""}`, [clientId]);
  const currentYear = new Date().getFullYear();
  const currentMonthNumber = new Date().getMonth() + 1;
  const pieColors = useMemo(
    () => ({
      Debi: "#2f9e44",
      Kredi: "#e67700",
      Bilanci: "#1c7ed6"
    }),
    []
  );

  const loadKpis = React.useCallback(async () => {
    if (!clientId) return;
    try {
      setKpisLoading(true);
      setKpisError(null);
      const data = await computeFinancialKpis(clientId, dateRange, fiscalYear);
      setKpis(data);
    } catch (e) {
      setKpisError("Ngarkimi i KPI-ve financiare dështoi.");
      setKpis(null);
    } finally {
      setKpisLoading(false);
    }
  }, [clientId, dateRange, fiscalYear]);

  const loadSeries = React.useCallback(async () => {
    if (!clientId) return;
    try {
      setSeriesLoading(true);
      const data = await getFinancialChartSeries(clientId, dateRange, fiscalYear);
      setSeries(data);
    } catch {
      setSeries({ balanceOverTime: [], monthlyMovement: [] });
    } finally {
      setSeriesLoading(false);
    }
  }, [clientId, dateRange, fiscalYear]);

  const loadTable = React.useCallback(async () => {
    if (!clientId) return;
    try {
      setTableLoading(true);
      setTableError(null);
      const data = await listFinancialTransactionsPage({
        clientId,
        fiscalYear,
        page: (paginationModel?.page || 0) + 1,
        pageSize: paginationModel?.pageSize || 50,
        sortKey: sortModel?.[0]?.field || "date",
        sortDir: sortModel?.[0]?.sort || "desc",
        filters: {
          dateRange
        }
      });
      setRows(data?.rows || []);
      setTotal(data?.total || 0);
    } catch (e) {
      setTableError("Ngarkimi i transaksioneve dështoi.");
      setRows([]);
      setTotal(0);
    } finally {
      setTableLoading(false);
    }
  }, [clientId, fiscalYear, paginationModel, sortModel, dateRange]);

  useEffect(() => {
    setPaginationModel((prev) => ({ ...(prev || { pageSize: 50 }), page: 0 }));
  }, [clientId, dateRange?.from, dateRange?.to]);

  useEffect(() => {
    loadKpis();
    loadSeries();
  }, [loadKpis, loadSeries]);

  useEffect(() => {
    loadTable();
  }, [loadTable]);

  useEffect(() => {
    const updateThemeColors = () => setThemeColors(readFinancialThemeColors());
    updateThemeColors();

    const observer = new MutationObserver((mutations) => {
      const hasThemeMutation = mutations.some((m) => m.attributeName === "data-theme" || m.attributeName === "data-theme-variant");
      if (hasThemeMutation) updateThemeColors();
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme", "data-theme-variant"]
    });

    return () => observer.disconnect();
  }, []);

  const runningRows = useMemo(() => {
    let bal = kpis?.openingBalance || 0;
    return (rows || []).map((r) => {
      bal += (r.debit || 0) - (r.credit || 0);
      return { ...r, runningBalance: bal };
    });
  }, [rows, kpis?.openingBalance]);

  const debitCreditBalanceData = useMemo(() => {
    const debit = toNumeric(kpis?.totalDebit);
    const credit = toNumeric(kpis?.totalCredit);
    const balance = debit - credit;
    return [
      { name: "Debi", value: debit },
      { name: "Kredi", value: credit },
      { name: "Bilanci", value: balance }
    ];
  }, [kpis?.totalCredit, kpis?.totalDebit]);

  const visibleDebitCreditBalanceData = useMemo(
    () => debitCreditBalanceData.filter((item) => pieSeriesVisibility[item.name] !== false),
    [debitCreditBalanceData, pieSeriesVisibility]
  );

  const monthlyReceivableCollectionData = useMemo(() => {
    const byMonth = new Map();
    (series?.monthlyMovement || []).forEach((item) => {
      const rawMonth = String(item?.month || "").trim();
      let monthNo = 0;
      if (/^\d{4}-\d{2}$/.test(rawMonth)) {
        monthNo = Number(rawMonth.slice(5, 7));
      } else {
        monthNo = Number(rawMonth);
      }

      if (monthNo >= 1 && monthNo <= 12) {
        const current = byMonth.get(monthNo) || { teArketueshme: 0, arketimet: 0 };
        current.teArketueshme += toNumeric(item?.debit);
        current.arketimet += toNumeric(item?.credit);
        byMonth.set(monthNo, current);
      }
    });

    return Array.from({ length: 12 }, (_unused, index) => {
      const monthNo = index + 1;
      const current = byMonth.get(monthNo) || { teArketueshme: 0, arketimet: 0 };
      return {
        month: String(monthNo),
        teArketueshme: current.teArketueshme,
        arketimet: current.arketimet
      };
    });
  }, [series?.monthlyMovement]);

  const hasDateFilter = Boolean(dateRange?.from || dateRange?.to);

  const balanceChartData = useMemo(() => {
    const openingOffset = toNumeric(kpis?.openingBalance);
    const fromBalanceSeries = (series?.balanceOverTime || []).map((item) => ({
      date: item?.date,
      balance: toNumeric(item?.balance),
      debit: toNumeric(item?.debit),
      credit: toNumeric(item?.credit),
      hasDebitCredit: item?.debit != null || item?.credit != null
    }));

    const fromMonthlyMovement = (() => {
      const rows = series?.monthlyMovement || [];
      if (!rows.length) return [];
      let monthlyRunningBalance = openingOffset;
      return rows.map((item) => {
        const debit = toNumeric(item?.debit);
        const credit = toNumeric(item?.credit);
        monthlyRunningBalance += debit - credit;
        const monthNo = Number(monthNumberLabel(item?.month));
        return {
          date: monthNo >= 1 && monthNo <= 12 ? `2000-${String(monthNo).padStart(2, "0")}-01` : "",
          balance: monthlyRunningBalance,
          debit,
          credit,
          hasDebitCredit: true
        };
      });
    })();

    const source = fromBalanceSeries.length
      ? fromBalanceSeries
      : fromMonthlyMovement;

    if (hasDateFilter && source.length) {
      const sorted = [...source]
        .filter((item) => String(item?.date || "").trim())
        .sort((a, b) => String(a?.date || "").localeCompare(String(b?.date || "")));

      let running = openingOffset;
      return sorted.map((item, index) => {
        const debit = toNumeric(item?.debit);
        const credit = toNumeric(item?.credit);

        if (item?.hasDebitCredit) {
          running += debit - credit;
        } else if (index === 0) {
          running = openingOffset + toNumeric(item?.balance);
        } else {
          const prevRaw = toNumeric(sorted[index - 1]?.balance);
          const currentRaw = toNumeric(item?.balance);
          running += currentRaw - prevRaw;
        }

        return {
          balance: running,
          xLabel: dayMonthLabel(item?.date),
          tooltipLabel: dayMonthLabel(item?.date)
        };
      });
    }

    const byMonth = new Map();
    source.forEach((item) => {
      const monthNo = Number(monthNumberLabel(item?.date));
      if (monthNo >= 1 && monthNo <= 12) {
        byMonth.set(monthNo, toNumeric(item?.balance));
      }
    });

    const monthLimit = Number(fiscalYear) === currentYear ? currentMonthNumber : 12;
    let carriedBalance = openingOffset;
    return Array.from({ length: monthLimit }, (_unused, i) => {
      const month = i + 1;
      if (byMonth.has(month)) carriedBalance = byMonth.get(month);
      return {
        xLabel: String(month),
        tooltipLabel: `Muaji ${month}`,
        balance: carriedBalance
      };
    });
  }, [series?.balanceOverTime, series?.monthlyMovement, hasDateFilter, fiscalYear, currentYear, currentMonthNumber, kpis?.openingBalance]);

  const balanceYAxisDomain = useMemo(() => {
    const values = (balanceChartData || []).map((d) => toNumeric(d?.balance));
    if (values.length === 0) return [-1, 1];

    const min = Math.min(...values);
    const max = Math.max(...values);

    if (!Number.isFinite(min) || !Number.isFinite(max)) return [-1, 1];

    if (min === max) {
      const pad = Math.max(1, Math.abs(min) * 0.1);
      return [min - pad, max + pad];
    }

    const spread = max - min;
    const pad = Math.max(1, spread * 0.08);
    return [min - pad, max + pad];
  }, [balanceChartData]);

  const monthlyLegendItems = useMemo(
    () => [
      { key: "teArketueshme", label: "Te arketueshme", color: themeColors.chartDebit },
      { key: "arketimet", label: "Arketimet", color: themeColors.chartCredit }
    ],
    [themeColors.chartCredit, themeColors.chartDebit]
  );

  const pieLegendItems = useMemo(
    () => [
      { key: "Debi", label: "Debi", color: pieColors.Debi },
      { key: "Kredi", label: "Kredi", color: pieColors.Kredi },
      { key: "Bilanci", label: "Bilanci", color: pieColors.Bilanci }
    ],
    [pieColors]
  );

  const lineLegendItems = useMemo(
    () => [{ key: "balance", label: "Bilanci", color: themeColors.chartLine }],
    [themeColors.chartLine]
  );

  const toggleMonthlySeries = React.useCallback((key) => {
    setMonthlySeriesVisibility((prev) => ({ ...prev, [key]: prev[key] === false }));
  }, []);

  const togglePieSeries = React.useCallback((key) => {
    setPieSeriesVisibility((prev) => ({ ...prev, [key]: prev[key] === false }));
  }, []);

  const transactionColumns = useMemo(() => ([
    {
      field: "rowNumber",
      headerName: "#",
      minWidth: 70,
      width: 80
    },
    {
      field: "status",
      headerName: "Status",
      minWidth: 150,
      width: 170,
      valueFormatter: (value) => getActionTypeLabel(value || "")
    },
    {
      field: "accountName",
      headerName: "Emri i llogarisë",
      minWidth: 230,
      width: 280
    },
    {
      field: "identificationNumber",
      headerName: "Numri identifikues",
      minWidth: 180,
      width: 210,
      valueFormatter: (value) => value || "—"
    },
    {
      field: "date",
      headerName: "Data",
      minWidth: 120,
      width: 130,
      valueFormatter: (value) => (value ? format(new Date(value), "dd.MM.yyyy") : "—")
    },
    {
      field: "description",
      headerName: "Përshkrimi",
      minWidth: 240,
      width: 280
    },
    {
      field: "debit",
      headerName: "Debit",
      minWidth: 130,
      width: 150,
      valueFormatter: (value) => money(value || 0)
    },
    {
      field: "credit",
      headerName: "Kredit",
      minWidth: 130,
      width: 150,
      valueFormatter: (value) => money(value || 0)
    },
    {
      field: "runningBalance",
      headerName: "Bilanci rrjedhës",
      minWidth: 160,
      width: 180,
      valueFormatter: (value) => money(value || 0)
    }
  ]), []);

  const transactionRows = useMemo(() => {
    return (runningRows || []).map((r, index) => ({
      id: r.id ?? `${r.journalID || "trx"}-${index}`,
      rowNumber: (paginationModel.page * paginationModel.pageSize) + index + 1,
      accountName: r.accountName || client?.AccountName || client?.name || "—",
      identificationNumber: r.identificationNumber || client?.IdentificationNumber || client?.identificationNumber || "—",
      status: r.status || r.actionType || r.ActionType || "",
      date: r.date,
      documentNo: r.documentNo || "—",
      description: r.description || "—",
      debit: r.debit || 0,
      credit: r.credit || 0,
      runningBalance: r.runningBalance || 0,
      rowInfo: r
    }));
  }, [runningRows, paginationModel.page, paginationModel.pageSize, client?.AccountName, client?.name, client?.IdentificationNumber, client?.identificationNumber]);

  const transactionSummaryFooter = useMemo(() => ({
    labelField: "description",
    label: "Totali",
    values: {
      debit: money((runningRows || []).reduce((sum, row) => sum + (Number(row?.debit) || 0), 0)),
      credit: money((runningRows || []).reduce((sum, row) => sum + (Number(row?.credit) || 0), 0)),
      runningBalance: money((runningRows || []).length ? (runningRows[runningRows.length - 1]?.runningBalance || 0) : (kpis?.openingBalance || 0))
    }
  }), [runningRows, kpis?.openingBalance]);

  const exportTotals = useMemo(() => ({
    debit: (runningRows || []).reduce((sum, row) => sum + (Number(row?.debit) || 0), 0),
    credit: (runningRows || []).reduce((sum, row) => sum + (Number(row?.credit) || 0), 0),
    runningBalance: (runningRows || []).length
      ? (runningRows[runningRows.length - 1]?.runningBalance || 0)
      : (kpis?.openingBalance || 0)
  }), [runningRows, kpis?.openingBalance]);

  const filtersSummary = useMemo(() => {
    const parts = [];
    if (dateRange?.from || dateRange?.to) parts.push(`Datë: ${dateRange?.from || "…"} → ${dateRange?.to || "…"}`);
    return parts.join(" | ");
  }, [dateRange]);

  const exportMeta = useMemo(() => {
    return {
      companyName: "Emri i kompanisë",
      companyAddress: "Rruga, Qyteti, Shteti",
      companyVat: "VAT: K12345678",
      generatedAtIso: new Date().toISOString(),
      generatedBy: "Përdoruesi aktual",
      reportTitle: `Kartela e Klientit — Financiare (${client?.code || ""})`,
      filtersSummary
    };
  }, [client?.code, filtersSummary]);

  const fetchAllRowsForExport = React.useCallback(async () => {
    const exportPageSize = Math.max(Number(total) || 0, (transactionRows || []).length, 1);
    const data = await listFinancialTransactionsPage({
      clientId,
      fiscalYear,
      page: 1,
      pageSize: exportPageSize,
      sortKey: sortModel?.[0]?.field || "date",
      sortDir: sortModel?.[0]?.sort || "desc",
      filters: {
        dateRange
      }
    });

    const exportRunningRows = (() => {
      let balance = kpis?.openingBalance || 0;
      return (data?.rows || []).map((row) => {
        balance += (row.debit || 0) - (row.credit || 0);
        return { ...row, runningBalance: balance };
      });
    })();

    return exportRunningRows.map((r, index) => ({
      id: r.id ?? `${r.journalID || "trx"}-${index}`,
      rowNumber: index + 1,
      status: getActionTypeLabel(r.status || r.actionType || r.ActionType || ""),
      accountName: r.accountName || client?.AccountName || client?.name || "—",
      identificationNumber: r.identificationNumber || client?.IdentificationNumber || client?.identificationNumber || "—",
      date: formatDateForDisplay(r.date),
      description: r.description || "—",
      debit: Number(r.debit || 0),
      credit: Number(r.credit || 0),
      runningBalance: Number(r.runningBalance || 0),
      rowInfo: r
    }));
  }, [client?.AccountName, client?.IdentificationNumber, client?.identificationNumber, client?.name, clientId, dateRange, fiscalYear, kpis?.openingBalance, sortModel, total, transactionRows]);

  const fetchExportRows = React.useCallback(async () => fetchAllRowsForExport(), [fetchAllRowsForExport]);

  const buildPdfDocument = React.useCallback(async () => {
    const exportRows = await fetchAllRowsForExport();
    if (!exportRows.length) return null;

    const doc = new jsPDF({ orientation: "landscape" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const horizontalMargin = 12;
    const availableTableWidth = pageWidth - (horizontalMargin * 2);
    const generatedLabel = formatDateForDisplay(new Date());

    const baseColumnStyles = {
      0: { cellWidth: 10, halign: "center" },
      1: { cellWidth: 24 },
      2: { cellWidth: 40 },
      3: { cellWidth: 28 },
      4: { cellWidth: 20, halign: "center" },
      5: { cellWidth: 56 },
      6: { cellWidth: 22, halign: "right" },
      7: { cellWidth: 22, halign: "right" },
      8: { cellWidth: 28, halign: "right" }
    };

    const baseTableWidth = Object.values(baseColumnStyles).reduce(
      (sum, style) => sum + Number(style.cellWidth || 0),
      0
    );

    const scaleRatio = baseTableWidth > 0 ? availableTableWidth / baseTableWidth : 1;
    const columnStyles = Object.entries(baseColumnStyles).reduce((acc, [key, style]) => {
      acc[key] = {
        ...style,
        cellWidth: Number((Number(style.cellWidth || 0) * scaleRatio).toFixed(3))
      };
      return acc;
    }, {});

    doc.setDrawColor(40, 73, 69);
    doc.setLineWidth(0.5);
    doc.line(12, 24, pageWidth - 12, 24);

    doc.setTextColor(31, 58, 54);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(17);
    doc.text("Kartela Financiare Analitike", 12, 16);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text([client?.AccountName || client?.name || "-", `Gjeneruar më: ${generatedLabel}`], pageWidth - 12, 14, { align: "right" });

    doc.autoTable({
      head: [["#", "Status", "Emri i llogarisë", "Numri identifikues", "Data", "Përshkrimi", "Debit", "Kredit", "Bilanci rrjedhës"]],
      body: exportRows.map((item) => ([
        item.rowNumber,
        item.status || "-",
        item.accountName || "-",
        item.identificationNumber || "-",
        item.date || "-",
        item.description || "-",
        formatCurrency(item.debit),
        formatCurrency(item.credit),
        formatCurrency(item.runningBalance)
      ])),
      foot: [[
        "",
        "",
        "",
        "",
        "",
        "Totali",
        formatCurrency(exportTotals.debit),
        formatCurrency(exportTotals.credit),
        formatCurrency(exportTotals.runningBalance)
      ]],
      startY: 30,
      showFoot: "lastPage",
      margin: { left: horizontalMargin, right: horizontalMargin, top: 16, bottom: 16 },
      theme: "grid",
      styles: {
        fontSize: 7.5,
        cellPadding: { top: 1.5, right: 1.5, bottom: 1.5, left: 1.5 },
        lineColor: [212, 226, 219],
        lineWidth: 0.1,
        textColor: [31, 58, 54],
        overflow: "linebreak"
      },
      headStyles: {
        fillColor: [40, 73, 69],
        textColor: [255, 255, 255],
        fontStyle: "bold",
        halign: "center",
        valign: "middle"
      },
      footStyles: {
        fillColor: [231, 240, 235],
        textColor: [31, 58, 54],
        fontStyle: "bold"
      },
      alternateRowStyles: {
        fillColor: [247, 250, 248]
      },
      didParseCell: (hookData) => {
        if (hookData.section !== "foot") return;
        if (hookData.column.index >= 6) {
          hookData.cell.styles.halign = "right";
        }
      },
      columnStyles
    });

    return doc;
  }, [client?.AccountName, client?.name, exportTotals.credit, exportTotals.debit, exportTotals.runningBalance, fetchAllRowsForExport]);

  const handleExportPDF = React.useCallback(async () => {
    const doc = await buildPdfDocument();
    if (!doc) return;
    doc.save(`kartela-financiare-${(client?.AccountName || client?.name || "klienti").replace(/\s+/g, "-").toLowerCase()}.pdf`);
  }, [buildPdfDocument, client?.AccountName, client?.name]);

  const handlePrintPDF = React.useCallback(async () => {
    const doc = await buildPdfDocument();
    if (!doc) return;

    const pdfBlob = doc.output("blob");
    const printUrl = URL.createObjectURL(pdfBlob);
    const printFrame = document.createElement("iframe");
    printFrame.style.position = "fixed";
    printFrame.style.left = "-10000px";
    printFrame.style.top = "0";
    printFrame.style.width = "1024px";
    printFrame.style.height = "768px";
    printFrame.style.border = "0";
    printFrame.style.opacity = "0";
    printFrame.style.pointerEvents = "none";

    const cleanup = () => {
      URL.revokeObjectURL(printUrl);
      if (printFrame.parentNode) printFrame.parentNode.removeChild(printFrame);
    };

    printFrame.onload = () => {
      const frameWindow = printFrame.contentWindow;
      if (!frameWindow) {
        cleanup();
        return;
      }

      window.setTimeout(() => {
        frameWindow.focus();
        frameWindow.onafterprint = cleanup;
        frameWindow.print();
        window.setTimeout(cleanup, 5000);
      }, 450);
    };

    document.body.appendChild(printFrame);
    printFrame.src = printUrl;
    window.setTimeout(cleanup, 60000);
  }, [buildPdfDocument]);

  const handleExportXlsx = React.useCallback(async () => {
    const exportRows = await fetchAllRowsForExport();
    if (!exportRows.length) return;

    const worksheet = XLSX.utils.json_to_sheet([
      ...exportRows.map((item) => ({
      "#": item.rowNumber,
      "Status": item.status,
      "Emri i llogarisë": item.accountName,
      "Numri identifikues": item.identificationNumber,
      "Data": item.date,
      "Përshkrimi": item.description,
      "Debit": formatCurrency(item.debit),
      "Kredit": formatCurrency(item.credit),
      "Bilanci rrjedhës": formatCurrency(item.runningBalance)
      })),
      {
        "#": "",
        "Status": "",
        "Emri i llogarisë": "",
        "Numri identifikues": "",
        "Data": "",
        "Përshkrimi": "Totali",
        "Debit": formatCurrency(exportTotals.debit),
        "Kredit": formatCurrency(exportTotals.credit),
        "Bilanci rrjedhës": formatCurrency(exportTotals.runningBalance)
      }
    ]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "KartelaFinanciare");
    XLSX.writeFile(workbook, `kartela-financiare-${(client?.AccountName || client?.name || "klienti").replace(/\s+/g, "-").toLowerCase()}.xlsx`);
  }, [client?.AccountName, client?.name, exportTotals.credit, exportTotals.debit, exportTotals.runningBalance, fetchAllRowsForExport]);

  const gridToolbarActions = useMemo(() => (
    <>
      <AddActionsButton
        text="Print"
        onClick={async () => {
          if (!canPrint) return;
          await handlePrintPDF();
          await logAuditEvent({
            action: "print",
            tab: "financial",
            clientId: clientId || "",
            occurredAtIso: new Date().toISOString(),
            details: filtersSummary
          });
        }}
        icon={<AiOutlinePrinter />}
        className="RSDG__toolButton SBP__gridActionBtn--print"
      />

      <AddActionsButton
        text="PDF"
        onClick={async () => {
          if (!canExport) return;
          await handleExportPDF();
          await logAuditEvent({
            action: "export_pdf",
            tab: "financial",
            clientId: clientId || "",
            occurredAtIso: new Date().toISOString(),
            details: filtersSummary
          });
        }}
        icon={<AiOutlineFilePdf />}
        className="RSDG__toolButton SBP__gridActionBtn--pdf"
      />

      <AddActionsButton
        text="XLSX"
        onClick={async () => {
          if (!canExport) return;
          await handleExportXlsx();
          await logAuditEvent({
            action: "export_xlsx",
            tab: "financial",
            clientId: clientId || "",
            occurredAtIso: new Date().toISOString(),
            details: filtersSummary
          });
        }}
        icon={<BsFiletypeXls />}
        className="RSDG__toolButton SBP__gridActionBtn--xlsx"
      />
    </>
  ), [canExport, canPrint, clientId, filtersSummary, handleExportPDF, handleExportXlsx, handlePrintPDF]);

  return (
    <div className="ftab-root">
      <div id={printId} className="print-container ftab-print-container">
        <div className="ftab-kpi-grid">
          {kpisLoading ? (
            [...Array(4)].map((_, i) => <div key={i} className="ftab-kpi-skeleton skeleton" />)
          ) : kpisError ? (
            <div className="ftab-kpi-error-box">
              {kpisError}
            </div>
          ) : (
            <>
              <KpiCard
                title="Bilanci hapës"
                value={money(kpis?.openingBalance || 0)}
                helperText="Bilanci fillestar për periudhën e zgjedhur."
                tone="neutral"
              />
              <KpiCard
                title="Debiti total (Faturim)"
                value={money(kpis?.totalDebit || 0)}
                helperText="Debitet totale (fatura, ngarkesa) në periudhë."
                tone="warning"
              />
              <KpiCard
                title="Krediti total (Pagesa)"
                value={money(kpis?.totalCredit || 0)}
                helperText="Kreditet totale (pagesa, nota krediti) në periudhë."
                tone="positive"
              />
              <KpiCard
                title="Bilanci aktual"
                value={money(kpis?.currentBalance || 0)}
                helperText="Hapës + Debit − Kredit."
                tone={(kpis?.currentBalance || 0) > 0 ? "warning" : "positive"}
              />
            </>
          )}
        </div>

        <div className="ftab-charts-grid">
          <Card className="ftab-line-card">
            <CardContent className="ftab-chart-320">
              {seriesLoading ? (
                <div className="ftab-chart-skeleton skeleton" />
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={monthlyReceivableCollectionData}>
                    <CartesianGrid strokeDasharray="3 3" stroke={themeColors.chartGrid} />
                    <XAxis dataKey="month" tick={{ fontSize: 11, fill: themeColors.chartAxis }} />
                    <YAxis tick={{ fontSize: 11, fill: themeColors.chartAxis }} />
                    <ReTooltip
                      content={<GenericChartTooltip valueFormatter={(value) => money(value || 0)} />}
                      labelFormatter={(label) => monthNameByNumber(label)}
                    />
                    <Legend
                      verticalAlign="bottom"
                      content={() => (
                        <InteractiveLegend
                          items={monthlyLegendItems}
                          onToggle={toggleMonthlySeries}
                          isHidden={(key) => monthlySeriesVisibility[key] === false}
                        />
                      )}
                    />
                    <Bar hide={monthlySeriesVisibility.teArketueshme === false} dataKey="teArketueshme" name="Te arketueshme" fill={themeColors.chartDebit} />
                    <Bar hide={monthlySeriesVisibility.arketimet === false} dataKey="arketimet" name="Arketimet" fill={themeColors.chartCredit} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="ftab-chart-320">
              {kpisLoading ? (
                <div className="ftab-chart-skeleton skeleton" />
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <ReTooltip
                      content={<GenericChartTooltip valueFormatter={(value) => money(value || 0)} />}
                    />
                    <Legend
                      verticalAlign="bottom"
                      height={28}
                      content={() => (
                        <InteractiveLegend
                          items={pieLegendItems}
                          onToggle={togglePieSeries}
                          isHidden={(key) => pieSeriesVisibility[key] === false}
                        />
                      )}
                    />
                    <Pie
                      data={visibleDebitCreditBalanceData}
                      dataKey="value"
                      nameKey="name"
                      cy="45%"
                      outerRadius={98}
                      label={PieValueLabel}
                      labelLine={false}
                    >
                      {visibleDebitCreditBalanceData.map((entry) => (
                        <Cell
                          key={`debit-credit-balance-${entry.name}`}
                          fill={entry.name === "Debi" ? pieColors.Debi : entry.name === "Kredi" ? pieColors.Kredi : pieColors.Bilanci}
                        />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          <Card className="ftab-bar-card">
            <CardHeader
              className="ftab-card-header-tight"
              title={
                <div>
                  <Typography className="ftab-card-title-sm">Bilanci në kohë</Typography>
                  <Typography className="ftab-card-subtitle-xs">Trend i bilancit sipas periudhës së zgjedhur</Typography>
                </div>
              }
            />
            <CardContent className="ftab-chart-280">
              {seriesLoading ? (
                <div className="ftab-chart-skeleton skeleton" />
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={balanceChartData} margin={{ top: 8, right: 8, left: 4, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={themeColors.chartGrid} />
                    <XAxis
                      dataKey="xLabel"
                      hide={false}
                      tick={{ fontSize: 11, fill: themeColors.chartAxis }}
                      allowDuplicatedCategory={false}
                      ticks={hasDateFilter ? undefined : ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12"]}
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: themeColors.chartAxis }}
                      width={78}
                      tickFormatter={moneyAxis}
                      domain={balanceYAxisDomain}
                    />
                    <ReTooltip
                      content={<BalanceChartTooltip />}
                      cursor={{ stroke: themeColors.chartCursor, strokeDasharray: "4 4", strokeWidth: 1 }}
                    />
                    <Legend
                      verticalAlign="bottom"
                      content={() => (
                        <InteractiveLegend
                          items={lineLegendItems}
                          onToggle={() => setBalanceSeriesVisible((prev) => !prev)}
                          isHidden={() => !balanceSeriesVisible}
                        />
                      )}
                    />
                    <Line
                      hide={!balanceSeriesVisible}
                      type="monotone"
                      dataKey="balance"
                      name="Bilanci"
                      stroke={themeColors.chartLine}
                      strokeWidth={3}
                      dot={{ r: 2, fill: themeColors.chartLine, stroke: themeColors.chartLine }}
                      connectNulls
                      activeDot={{ r: 5, fill: themeColors.chartLine, stroke: themeColors.cardBg, strokeWidth: 2 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="ftab-transactions SBP__page">
          <div className="SBP__content">
            <div className="SBP__reportGrid ftab-report-grid">
              {tableError ? (
                <div className="ftab-table-error-box">
                  {tableError}
                </div>
              ) : null}
              <ReusableAccountBooksGrid
                columns={transactionColumns}
                rows={transactionRows}
                loading={tableLoading}
                rowCount={total}
                height={650}
                paginationModel={paginationModel}
                setPaginationModel={setPaginationModel}
                sortModel={sortModel}
                setSortModel={setSortModel}
                filterModel={filterModel}
                setFilterModel={setFilterModel}
                quickFilter={quickFilter}
                setQuickFilter={setQuickFilter}
                showQuickSearch={false}
                getExportRows={fetchExportRows}
                toolbarActions={gridToolbarActions}
                showViewAction={false}
                showEditAction={false}
                showDeleteAction={false}
                showExportButton={false}
                emptyMessage="Nuk u gjet asnjë transaksion për filtrat e zgjedhur."
                summaryFooter={transactionSummaryFooter}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
